var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { entityToWallet, OnRampProvider, OnRampAsset, Network, EnabledFlow, } from '@usecapsule/web-sdk';
import { useCapsuleStore, useModalStore, useUserInfoStore } from '../../stores/index.js';
import { ModalStep } from '../../utils/steps.js';
import { Body } from '../Body/Body.js';
import { Footer } from '../Footer/Footer.js';
import { DEFAULTS } from '../../constants/defaults.js';
import { useGoBack } from '../../hooks/useGoBack.js';
import { openPopup } from '../../utils/openPopup.js';
function isRampConfig(config) {
    return 'hostApiKey' in config;
}
const AssetNetworks = {
    [OnRampAsset.SOLANA]: Network.SOLANA,
    [OnRampAsset.ATOM]: Network.COSMOS,
    [OnRampAsset.CELO]: Network.CELO,
    [OnRampAsset.POLYGON]: Network.POLYGON,
};
const AssetMap = {
    SOLANA: OnRampAsset.SOLANA,
    SOL: OnRampAsset.SOLANA,
    ATOM: OnRampAsset.ATOM,
    CELO: OnRampAsset.CELO,
    POLYGON: OnRampAsset.POLYGON,
    MATIC: OnRampAsset.POLYGON,
    USDC: OnRampAsset.USDC,
    ETH: OnRampAsset.ETHEREUM,
    ETHEREUM: OnRampAsset.ETHEREUM,
};
export const ModalContent = forwardRef(({ onRampConfig: propsOnRampConfig, twoFactorAuthEnabled = false, recoverySecretStepEnabled = false, oAuthMethods, disableEmailLogin, disablePhoneLogin, onClose, onRampTestMode, loginTransitionOverride, createWalletOverride, }, ref) => {
    const capsule = useCapsuleStore(state => state.capsule);
    const currentStep = useModalStore(state => state.step);
    const webAuthURLForLogin = useModalStore(state => state.webAuthURLForLogin);
    const webAuthURLForCreate = useModalStore(state => state.webAuthURLForCreate);
    const passwordUrlForLogin = useModalStore(state => state.passwordUrlForLogin);
    const isLogin = useModalStore(state => state.isLogin());
    const popupWindow = useModalStore(state => state.popupWindow);
    const onRampConfig = useModalStore(state => state.onRampConfig);
    const setStep = useModalStore(state => state.setStep);
    const setBiometricLocationHints = useModalStore(state => state.setBiometricLocationHints);
    const setWebAuthURLForLogin = useModalStore(state => state.setWebAuthURLForLogin);
    const setWebAuthURLForCreate = useModalStore(state => state.setWebAuthURLForCreate);
    const setPopupWindow = useModalStore(state => state.setPopupWindow);
    const setPasswordUrlForCreate = useModalStore(state => state.setPasswordUrlForCreate);
    const setPasswordUrlForLogin = useModalStore(state => state.setPasswordUrlForLogin);
    const setSupportedAuthMethods = useModalStore(state => state.setSupportedAuthMethods);
    const setOnRampConfig = useModalStore(state => state.setOnRampConfig);
    const setRecoveryShare = useUserInfoStore(state => state.setRecoveryShare);
    const goBack = useGoBack();
    const loginTimeout = useRef();
    const createAccountTimeout = useRef();
    const [walletCreationInProgress, setWalletCreationInProgress] = useState(false);
    useImperativeHandle(ref, () => {
        return {
            handleModalClose() {
                handleClose();
            },
        };
    }, []);
    const is2FASetup = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!twoFactorAuthEnabled) {
            return true;
        }
        try {
            const { isSetup } = yield capsule.check2FAStatus();
            return isSetup;
        }
        catch (error) {
            console.error('An error occurred while checking 2FA:', error);
            return false;
        }
    });
    function awaitLoginTransition() {
        return __awaiter(this, void 0, void 0, function* () {
            const { isComplete, isError, needsWallet } = yield capsule.waitForLoginAndSetup(popupWindow);
            setPopupWindow(undefined);
            if (isError) {
                goBack();
                return;
            }
            if (isComplete) {
                setWebAuthURLForLogin('');
                setPasswordUrlForLogin('');
                setSupportedAuthMethods(new Set());
                setBiometricLocationHints();
                if (needsWallet) {
                    setStep(ModalStep.AWAITING_WALLET_CREATION);
                }
                else {
                    if (yield is2FASetup()) {
                        setStep(ModalStep.LOGIN_DONE);
                    }
                    else {
                        setStep(ModalStep.SETUP_2FA);
                    }
                }
            }
        });
    }
    function awaitWalletCreationTransition() {
        return __awaiter(this, void 0, void 0, function* () {
            const isComplete = yield capsule.waitForAccountCreation();
            if (isComplete) {
                setWebAuthURLForCreate('');
                setPasswordUrlForCreate('');
                setStep(ModalStep.AWAITING_WALLET_CREATION);
            }
        });
    }
    // generate/claim wallet once we know it's account creation
    useEffect(() => {
        if (currentStep !== ModalStep.AWAITING_WALLET_CREATION || walletCreationInProgress) {
            return;
        }
        function genWallet() {
            return __awaiter(this, void 0, void 0, function* () {
                setWalletCreationInProgress(true);
                let recoverySecret, walletIds;
                if (!createWalletOverride) {
                    const created = yield capsule.waitForPasskeyAndCreateWallet();
                    recoverySecret = created.recoverySecret;
                    walletIds = created.walletIds;
                }
                else {
                    const created = yield createWalletOverride(capsule);
                    const fetchedWallets = (yield capsule.fetchWallets()).filter(wallet => !!wallet.address);
                    const newWallets = {};
                    for (const wallet of fetchedWallets) {
                        newWallets[wallet.id] = Object.assign(Object.assign({}, entityToWallet(wallet)), { signer: '' });
                    }
                    capsule.setWallets(newWallets);
                    recoverySecret = created.recoverySecret;
                    walletIds = created.walletIds;
                }
                yield capsule.setCurrentWalletIds(walletIds);
                if (recoverySecretStepEnabled) {
                    setRecoveryShare(recoverySecret);
                }
                setWalletCreationInProgress(false);
                if (!recoverySecret || !recoverySecretStepEnabled) {
                    setStep(ModalStep.WALLET_CREATION_DONE);
                }
                else {
                    setStep(ModalStep.SECRET);
                }
            });
        }
        genWallet();
    }, [isLogin, currentStep]);
    function createAccountWithPassword() {
        return __awaiter(this, void 0, void 0, function* () {
            clearTimeout(createAccountTimeout.current);
            createAccountTimeout.current = window.setTimeout(awaitWalletCreationTransition, DEFAULTS.POLLING_INTERVAL_MS);
            setStep(ModalStep.PASSWORD_CREATION);
        });
    }
    function createAccountWithPasskey() {
        return __awaiter(this, void 0, void 0, function* () {
            clearTimeout(createAccountTimeout.current);
            createAccountTimeout.current = window.setTimeout(awaitWalletCreationTransition, DEFAULTS.POLLING_INTERVAL_MS);
            openPopup(webAuthURLForCreate, 'CapsulePasskey', 'CREATE_PASSKEY');
            setStep(ModalStep.AWAITING_BIOMETRIC_CREATION);
        });
    }
    // wait for login auth to do post login setup
    useEffect(() => {
        if (webAuthURLForLogin || passwordUrlForLogin) {
            if (loginTransitionOverride) {
                function loginOverride() {
                    return __awaiter(this, void 0, void 0, function* () {
                        yield loginTransitionOverride(capsule);
                        setWebAuthURLForLogin('');
                        setPasswordUrlForLogin('');
                        setBiometricLocationHints();
                        if (yield is2FASetup()) {
                            setStep(ModalStep.LOGIN_DONE);
                        }
                        else {
                            setStep(ModalStep.SETUP_2FA);
                        }
                    });
                }
                loginOverride();
                return;
            }
            loginTimeout.current = window.setTimeout(awaitLoginTransition, DEFAULTS.LOGGIN_POLLING_DELAY_MS);
        }
        return () => {
            window.clearTimeout(loginTimeout.current);
            capsule.exitLogin();
        };
    }, [webAuthURLForLogin, passwordUrlForLogin, popupWindow]);
    const handleClose = () => {
        onClose();
    };
    useEffect(() => {
        if (![ModalStep.BIOMETRIC_CREATION, ModalStep.AWAITING_BIOMETRIC_CREATION].includes(currentStep)) {
            capsule.exitAccountCreation();
        }
        if (![ModalStep.BIOMETRIC_LOGIN, ModalStep.AWAITING_BIOMETRIC_LOGIN].includes(currentStep)) {
            capsule.exitLogin();
        }
        if (![ModalStep.AWAITING_OAUTH, ModalStep.FARCASTER_OAUTH].includes(currentStep)) {
            capsule.exitOAuth();
        }
    }, [currentStep]);
    useEffect(() => {
        if (!onRampConfig) {
            capsule.ctx.capsuleClient
                .getOnRampConfig()
                .then(res => {
                var _a, _b;
                if (!!propsOnRampConfig) {
                    const { enabledFlows, network, asset, providers, testMode } = propsOnRampConfig;
                    const rampConfig = providers.find(config => isRampConfig(config));
                    setOnRampConfig({
                        isBuyEnabled: !enabledFlows || enabledFlows.some(str => EnabledFlow[str] === EnabledFlow.BUY),
                        isReceiveEnabled: !enabledFlows || enabledFlows.some(str => EnabledFlow[str] === EnabledFlow.RECEIVE),
                        isWithdrawEnabled: !enabledFlows || enabledFlows.some(str => EnabledFlow[str] === EnabledFlow.WITHDRAW),
                        allowedAssets: network
                            ? { [Network[network]]: asset ? [AssetMap[asset]] : true }
                            : asset
                                ? { [(_a = AssetNetworks[AssetMap[asset]]) !== null && _a !== void 0 ? _a : Network.ETHEREUM]: [AssetMap[asset]] }
                                : res.allowedAssets,
                        assetInfo: res.assetInfo,
                        providers: providers.map(({ id }) => OnRampProvider[id]),
                        rampApiKey: (_b = rampConfig === null || rampConfig === void 0 ? void 0 : rampConfig.hostApiKey) !== null && _b !== void 0 ? _b : res.rampApiKey,
                        testMode: testMode !== null && testMode !== void 0 ? testMode : onRampTestMode,
                    });
                    return;
                }
                setOnRampConfig(Object.assign(Object.assign({}, res), { testMode: onRampTestMode }));
            })
                .catch();
        }
    }, []);
    useEffect(() => {
        if (!!onRampConfig) {
            setOnRampConfig(Object.assign(Object.assign({}, onRampConfig), { testMode: onRampTestMode }));
        }
    }, [onRampTestMode]);
    useEffect(() => {
        return () => {
            capsule.exitLoops();
        };
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(Body, { oAuthMethods: oAuthMethods, twoFactorAuthEnabled: twoFactorAuthEnabled, disableEmailLogin: disableEmailLogin, disablePhoneLogin: disablePhoneLogin, onClose: handleClose, createAccountWithPasskey: createAccountWithPasskey, createAccountWithPassword: createAccountWithPassword }), _jsx(Footer, {})] }));
});
